import React, { useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import { Box } from '@mui/material';

import { useClickOutside } from '../../../hooks/use-common-actions';
import './datepicker.css';
import { DialogCustom } from '../../common/dialog-custom';

import type { RendererFunction } from '../../common/handsontable.types';

/**
 * Component Editor Props interface for Handsontable editor integration
 */
interface ComponentEditorProps {
  modelValue: { value: unknown };
  onUpdateModelValue: (newValue: string) => void;
  onFinished: () => void;
  cellElement?: HTMLElement;
}

/**
 * Position datepicker popup with smart positioning (above if not enough space below)
 * @param popupElement - The popup element to position
 * @param cellElement - The cell element to position relative to
 */
const positionDatepickerPopup = (
  popupElement: HTMLDialogElement | null,
  cellElement: HTMLElement
): void => {
  if (!popupElement) return;

  const rect = cellElement.getBoundingClientRect();
  const popupRect = popupElement.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  // Estimate popup dimensions (two calendars side by side)
  // Each calendar is approximately 280px wide, plus padding and gap
  const estimatedPopupWidth = 600;
  const estimatedPopupHeight = 400;

  // Use actual dimensions if available, otherwise use estimates
  const popupHeight = popupRect.height > 0 ? popupRect.height : estimatedPopupHeight;
  const popupWidth = popupRect.width > 0 ? popupRect.width : estimatedPopupWidth;

  // Calculate space below and above the cell
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;

  // Position horizontally - ensure it doesn't go off-screen
  let left = rect.left;
  if (left + popupWidth > viewportWidth) {
    // If popup would go off-screen to the right, align to the right edge
    left = Math.max(0, viewportWidth - popupWidth - 16); // 16px padding from edge
  }
  if (left < 0) {
    left = 16; // 16px padding from left edge
  }

  // Position vertically - show above if not enough space below
  let top: number;
  if (spaceBelow < popupHeight + 4 && spaceAbove > spaceBelow) {
    // Not enough space below, but more space above - position above
    top = rect.top - popupHeight - 4;
  } else {
    // Enough space below or more space below than above - position below
    top = rect.bottom + 4;
  }

  // Ensure popup doesn't go off-screen vertically
  if (top < 0) {
    top = 0; // 16px padding from top
  } else if (top + popupHeight > viewportHeight) {
    top = viewportHeight - popupHeight - 16; // 16px padding from bottom
  }

  popupElement.style.left = `${left}px`;
  popupElement.style.top = `${top}px`;
};

/**
 * Convert date from YYYY-MM-DD to YY/MM/DD format
 */
export const formatDateToYYMMDD = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    const date = dayjs(dateStr);
    if (!date.isValid()) return dateStr; // Return original if invalid
    const year = date.year().toString().slice(-2); // Last 2 digits of year
    const month = String(date.month() + 1).padStart(2, '0');
    const day = String(date.date()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  } catch {
    return dateStr;
  }
};

/**
 * Convert date from YY/MM/DD to YYYY-MM-DD format
 */
export const parseDateFromYYMMDD = (dateStr: string | null | undefined): string => {
  if (!dateStr?.trim()) return '';
  try {
    // Try to parse YY/MM/DD format
    const parts = dateStr.trim().split('/');
    if (parts.length === 3) {
      const year = Number.parseInt(parts[0], 10);
      const month = Number.parseInt(parts[1], 10);
      const day = Number.parseInt(parts[2], 10);

      // Validate month (01-12)
      if (month < 1 || month > 12) {
        return '';
      }

      // Validate day (01-31)
      if (day < 1 || day > 31) {
        return '';
      }

      // Convert 2-digit year to 4-digit (always 20XX)
      // Init 2 first digits of Year = 20
      const fullYear = 2000 + year;

      const date = dayjs(
        `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      );

      // Check if date is valid and matches input (prevents dayjs auto-normalization)
      // e.g., 31/02/2024 should not become 02/03/2024
      if (date.isValid()) {
        // Verify that parsed date matches input to prevent normalization
        if (date.year() === fullYear && date.month() + 1 === month && date.date() === day) {
          return date.format('YYYY-MM-DD');
        }
      }
    }

    // If already in YYYY-MM-DD format, validate and return
    const date = dayjs(dateStr);
    if (date.isValid()) {
      return date.format('YYYY-MM-DD');
    }

    return '';
  } catch {
    return '';
  }
};

interface DatepickerDialogContentProps {
  currentMonth: Date;
  selectedDate: Date | null;
  onDateClick: (date: Date) => void;
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

/**
 * Shared dialog content component for datepicker popup
 * Contains two month calendars side by side
 */
export const DatepickerDialogContent: React.FC<DatepickerDialogContentProps> = ({
  currentMonth,
  selectedDate,
  onDateClick,
  onPrevMonth,
  onNextMonth,
}) => {
  const month2 = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);

  return (
    <Box className='datepicker-popup'>
      <MonthCalendar
        monthDate={currentMonth}
        selectedDate={selectedDate}
        onDateClick={onDateClick}
        onPrevMonth={onPrevMonth}
        showPrevButton={true}
      />
      <MonthCalendar
        monthDate={month2}
        selectedDate={selectedDate}
        onDateClick={onDateClick}
        onNextMonth={onNextMonth}
        showNextButton={true}
      />
    </Box>
  );
};

interface MonthCalendarProps {
  monthDate: Date;
  selectedDate: Date | null;
  onDateClick: (date: Date) => void;
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
  showPrevButton?: boolean;
  showNextButton?: boolean;
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({
  monthDate,
  selectedDate,
  onDateClick,
  onPrevMonth,
  onNextMonth,
  showPrevButton = false,
  showNextButton = false,
}) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const dayNames = ['日', '月', '火', '水', '木', '金', '土'];

  // Extract nested ternary for day name className
  const getDayNameClassName = (index: number): string => {
    if (index === 0) {
      return 'datepicker-day-name datepicker-day-name-sunday';
    }
    if (index === 6) {
      return 'datepicker-day-name datepicker-day-name-saturday';
    }
    return 'datepicker-day-name';
  };

  return (
    <div className='datepicker-month-calendar'>
      <div className='datepicker-month-header'>
        {showPrevButton && onPrevMonth ? (
          <button type='button' className='datepicker-nav-button' onClick={onPrevMonth}>
            &lt;
          </button>
        ) : (
          <div className='datepicker-nav-spacer' />
        )}

        <div className='datepicker-month-label'>
          {year}年{month + 1}月
        </div>

        {showNextButton && onNextMonth ? (
          <button type='button' className='datepicker-nav-button' onClick={onNextMonth}>
            &gt;
          </button>
        ) : (
          <div className='datepicker-nav-spacer' />
        )}
      </div>

      <div className='datepicker-day-names'>
        {dayNames.map((dayName, index) => (
          <div key={dayName} className={getDayNameClassName(index)}>
            {dayName}
          </div>
        ))}
      </div>

      <div className='datepicker-calendar-grid'>
        {/* Empty cells for days before the first day of the month */}
        {Array.from({ length: startingDayOfWeek }).map((_, i) => (
          <div key={`empty-${year}-${month}-${i}`} className='datepicker-day-cell' />
        ))}

        {/* Current month days */}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const fullDate = new Date(year, month, day);
          const dayOfWeek = fullDate.getDay();
          const isSelected =
            selectedDate !== null &&
            fullDate.getFullYear() === selectedDate.getFullYear() &&
            fullDate.getMonth() === selectedDate.getMonth() &&
            fullDate.getDate() === selectedDate.getDate();

          return (
            <DayCell
              key={day}
              day={day}
              fullDate={fullDate}
              dayOfWeek={dayOfWeek}
              isSelected={isSelected}
              onDateClick={onDateClick}
            />
          );
        })}
      </div>
    </div>
  );
};

interface DayCellProps {
  day: number;
  fullDate: Date;
  dayOfWeek: number;
  isSelected: boolean;
  onDateClick: (date: Date) => void;
}

const DayCell: React.FC<DayCellProps> = ({ day, fullDate, dayOfWeek, isSelected, onDateClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getDayClassName = () => {
    if (isSelected) {
      return 'datepicker-day-cell datepicker-day-selected';
    }
    if (dayOfWeek === 0) {
      return 'datepicker-day-cell datepicker-day-sunday';
    }
    if (dayOfWeek === 6) {
      return 'datepicker-day-cell datepicker-day-saturday';
    }
    return 'datepicker-day-cell';
  };

  const getDayStyle = (): React.CSSProperties => {
    if (isSelected) {
      return {
        backgroundColor: '#0047BA',
        color: '#ffffff',
      };
    }
    if (isHovered) {
      return {
        backgroundColor: '#e3f2fd',
      };
    }
    return {};
  };

  return (
    <button
      type='button'
      className={getDayClassName()}
      style={getDayStyle()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onDateClick(fullDate)}
      aria-label={`Select date ${dayjs(fullDate).format('YYYY-MM-DD')}`}
    >
      {day}
    </button>
  );
};
/**
 * Custom renderer for datepicker column to display date in YY/MM/DD format
 * Used in Handsontable to display date cells with formatted date
 */
export const datepickerRenderer: RendererFunction = (_instance, td, _row, _col, _prop, value) => {
  // Clear existing content
  td.innerHTML = '';

  // Format the date for display
  const displayValue = formatDateToYYMMDD(
    typeof value === 'string' || value == null ? value : String(value)
  );

  // Create container for text
  const container = document.createElement('div');
  container.style.display = 'flex';
  container.style.alignItems = 'center';
  container.style.width = '100%';
  container.style.padding = '4px 8px';

  // Create text element
  const textSpan = document.createElement('span');
  textSpan.textContent = displayValue;
  textSpan.style.flex = '1';
  textSpan.style.overflow = 'hidden';
  textSpan.style.textOverflow = 'ellipsis';
  textSpan.style.whiteSpace = 'nowrap';

  container.appendChild(textSpan);
  td.appendChild(container);

  // Prevent Chrome Translate from misreading YY/MM/DD as DD/MM/YY dates
  td.setAttribute('translate', 'no');
  // Keep default Handsontable styling
  td.className = '';
  delete td.dataset.value;

  return td;
};

/**
 * Datepicker Editor Component for Handsontable
 * Compatible with ComponentEditorProps interface
 * This component is used with createReactComponentEditorClass
 */
export const DatepickerEditor: React.FC<ComponentEditorProps> = ({
  modelValue,
  onUpdateModelValue,
  onFinished,
  cellElement,
}) => {
  const popupRef = useRef<HTMLDialogElement>(null);

  // Get value from modelValue
  const value = typeof modelValue?.value === 'string' ? modelValue.value : '';

  // Store original value
  const originalValueRef = useRef<string>(value || '');

  // Initialize dates
  const initialDate = (() => {
    const dateStr = parseDateFromYYMMDD(value);
    if (dateStr) {
      const parsedDate = dayjs(dateStr).toDate();
      originalValueRef.current = value || '';
      return parsedDate;
    }
    // If no value, default to today's date
    const today = new Date();
    const todayFormatted = formatDateToYYMMDD(dayjs(today).format('YYYY-MM-DD'));
    originalValueRef.current = todayFormatted;
    // Update modelValue with default value
    onUpdateModelValue(todayFormatted);
    return today;
  })();

  const [currentMonth, setCurrentMonth] = useState<Date>(initialDate);
  const [selectedDate, setSelectedDate] = useState<Date | null>(initialDate);

  const handleClose = useCallback(
    (save: boolean, dateToSave?: Date) => {
      if (save) {
        // Use dateToSave if provided (from click), otherwise use selectedDate state
        const date = dateToSave || selectedDate;
        if (date) {
          const newValue = formatDateToYYMMDD(dayjs(date).format('YYYY-MM-DD'));
          // Update modelValue through editor callback
          onUpdateModelValue(newValue);
          // Finish editing - this will trigger Handsontable's afterChange
          onFinished();
        } else {
          onFinished();
        }
      } else {
        // If not saving, just finish editing without changing value
        onFinished();
      }
    },
    [selectedDate, onUpdateModelValue, onFinished]
  );

  // Position popup with smart positioning (above if not enough space below)
  useEffect(() => {
    if (popupRef.current && cellElement) {
      // Initial positioning with estimated dimensions
      positionDatepickerPopup(popupRef.current, cellElement);

      // Capture cellElement in a variable for use in nested callbacks
      const currentCellElement = cellElement;

      // Recalculate after popup is fully rendered to use actual dimensions
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          if (popupRef.current) {
            positionDatepickerPopup(popupRef.current, currentCellElement);
          }
        });
      });
    }
  }, [cellElement]);

  // Handle click outside - use mousedown to catch scrollbar clicks
  useClickOutside(popupRef, cellElement, () => handleClose(false), 'mousedown');

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    // Pass date directly to handleClose to avoid async state issue
    handleClose(true, date);
  };

  return (
    <DialogCustom cellElement={cellElement} onFinished={onFinished}>
      <DatepickerDialogContent
        currentMonth={currentMonth}
        selectedDate={selectedDate}
        onDateClick={handleDateClick}
        onPrevMonth={handlePrevMonth}
        onNextMonth={handleNextMonth}
      />
    </DialogCustom>
  );
};
